import type { StatusTone } from '@noor/ui';
import type { OrderTransaction } from '../model/order-schema';

/**
 * Transaction + courier-payment presentation, ported from cpanel
 * (`transaction_statuses` / `transaction_types` data + `orderCourierPaymentText`
 * + the type-3 label override). The entity layer stays React-free: each lookup
 * returns a stable `code` the UI turns into an i18n key, a `tone` for the v2
 * `StatusBadge`, and an icon slug the UI maps to a lucide component.
 *
 * Russian source labels are kept as fallbacks (the live cpanel copy), but the
 * UI should prefer `t('orders:transaction.*')` keyed off `code`.
 */

export type TransactionStatusCode =
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'pending'
  | 'unknown';

export interface TransactionStatusMeta {
  code: TransactionStatusCode;
  tone: StatusTone;
  /** Lucide icon slug (mapped in the UI). */
  icon: string;
  /** Russian source label (i18n fallback). */
  label: string;
}

/** Backend numeric status code → presentation. (cpanel `transaction_statuses`.) */
const TRANSACTION_STATUS_BY_NUMBER: Record<number, TransactionStatusMeta> = {
  // Keep the backend codes aligned with cpanel's transaction_statuses:
  // 1=succeeded, 2=failed, 3=cancelled, 4=pending.
  1: { code: 'succeeded', tone: 'success', icon: 'check', label: 'Успешно' },
  2: { code: 'succeeded', tone: 'success', icon: 'check', label: 'Успешно' },
  3: { code: 'cancelled', tone: 'neutral', icon: 'ban', label: 'Отменено' },
  4: { code: 'pending', tone: 'danger', icon: 'clock', label: 'В ожидании' },
};

const UNKNOWN_STATUS: TransactionStatusMeta = {
  code: 'unknown',
  tone: 'neutral',
  icon: 'help-circle',
  label: '—',
};

/** Resolve a transaction's numeric status to its presentation meta. */
export function transactionStatusMeta(
  status: number | null | undefined,
): TransactionStatusMeta {
  if (status == null) return UNKNOWN_STATUS;
  return TRANSACTION_STATUS_BY_NUMBER[status] ?? UNKNOWN_STATUS;
}

export type TransactionTypeCode =
  | 'topup'
  | 'withdrawal'
  | 'order_profit'
  | 'order_sum'
  | 'courier_received'
  | 'bonus'
  | 'correction'
  | 'unknown';

export interface TransactionTypeMeta {
  code: TransactionTypeCode;
  /**
   * Semantic status token (one of the `StatusBadge` tones). The UI renders the
   * chip with token classes (`bg-status-<tone>/15 text-status-<tone>`) so it
   * passes WCAG contrast in both themes — never a raw hex.
   */
  tone: StatusTone;
  /** Russian source label (i18n fallback). */
  label: string;
}

const TRANSACTION_TYPE_BY_NUMBER: Record<number, TransactionTypeMeta> = {
  1: { code: 'topup', tone: 'success', label: 'Пополнение' },
  2: { code: 'withdrawal', tone: 'danger', label: 'Списание' },
  3: { code: 'order_profit', tone: 'active', label: 'Доход с заказа' },
  4: { code: 'bonus', tone: 'active', label: 'Бонус' },
  5: { code: 'correction', tone: 'pending', label: 'Коррекция' },
};

const UNKNOWN_TYPE: TransactionTypeMeta = {
  code: 'unknown',
  tone: 'neutral',
  label: '—',
};

/**
 * Resolve a transaction's type. Type 3 (order profit) is context-dependent
 * (cpanel parity): a vendor-side row reads "order sum", a courier-side row reads
 * "courier received", otherwise the plain profit label.
 */
export function transactionTypeMeta(tx: OrderTransaction): TransactionTypeMeta {
  const base =
    tx.type == null
      ? UNKNOWN_TYPE
      : (TRANSACTION_TYPE_BY_NUMBER[tx.type] ?? UNKNOWN_TYPE);
  if (tx.type === 3) {
    if (tx.vendor_id != null)
      return { ...base, code: 'order_sum', label: 'Сумма заказа' };
    if (tx.courier_id != null)
      return { ...base, code: 'courier_received', label: 'Курьер получил' };
  }
  return base;
}

/**
 * Whether a transaction's cancel action is available, ported verbatim:
 * BALANCE-paid courier order-profit rows that aren't already cancelled.
 */
export function canCancelTransaction(
  tx: OrderTransaction,
  paymentType: string | null | undefined,
): boolean {
  return (
    paymentType === 'BALANCE' && tx.type === 3 && tx.courier_id != null && tx.status !== 3
  );
}

export type CourierPaymentCode =
  | 'not_paid'
  | 'pending_cash'
  | 'paid_cash'
  | 'pending_balance'
  | 'processing_balance'
  | 'paid'
  | 'paid_balance'
  | 'debt'
  | 'debt_repaid'
  | 'courier_choice'
  | 'unknown';

export interface CourierPaymentMeta {
  code: CourierPaymentCode;
  tone: StatusTone;
  label: string;
}

/**
 * `courier_payment_status` → text (cpanel `orderCourierPaymentText`). Drives the
 * client card's "product status" line + the change-payment gate (codes 1/3).
 */
const COURIER_PAYMENT_BY_NUMBER: Record<number, CourierPaymentMeta> = {
  0: { code: 'not_paid', tone: 'danger', label: 'Не оплачено' },
  1: { code: 'pending_cash', tone: 'danger', label: 'Ожидает наличные' },
  2: { code: 'paid_cash', tone: 'success', label: 'Оплачено наличными' },
  3: { code: 'pending_balance', tone: 'pending', label: 'Ожидает с баланса' },
  4: { code: 'processing_balance', tone: 'active', label: 'Обработка с баланса' },
  5: { code: 'paid', tone: 'success', label: 'Оплачено с баланса' },
  6: { code: 'debt', tone: 'danger', label: 'Долг' },
  7: { code: 'debt_repaid', tone: 'success', label: 'Долг погашен' },
  8: { code: 'courier_choice', tone: 'neutral', label: 'Курьеру приходится выбирать' },
};

const UNKNOWN_PAYMENT: CourierPaymentMeta = {
  code: 'unknown',
  tone: 'neutral',
  label: '—',
};

/** Resolve a courier-payment-status code to its presentation meta. */
export function courierPaymentMeta(
  status: number | null | undefined,
): CourierPaymentMeta {
  if (status == null) return UNKNOWN_PAYMENT;
  return COURIER_PAYMENT_BY_NUMBER[status] ?? UNKNOWN_PAYMENT;
}

/** Stages and statuses where the courier-payment type may be changed (cpanel). */
export function canChangeCourierPayment(
  status: number | null | undefined,
  stage: number,
): boolean {
  // courier_payment_status ∈ {1,3} AND stage ∈ {PERFORMER_FOUND(7), PICK_UP_ARRIVED(8)}
  return (status === 1 || status === 3) && (stage === 7 || stage === 8);
}
